import React, { useEffect } from 'react';
import { useLang } from '../i18n/LangContext';
import { BRAND } from '../data/siteData';
import EmergencyForm from '../components/sections/EmergencyForm';
import Footer from '../components/sections/Footer';
import SEO from '../components/SEO';
import { captureAttribution } from '../utils/leadTracking';
import { Siren, Clock, Phone, MessageCircle, ArrowLeft } from 'lucide-react';

const waNumber = '1' + (BRAND.phone || '').replace(/\D/g, '');

export default function EmergencyPage() {
  const { lang, setLang, t } = useLang();
  const e = t.emergency;

  useEffect(() => { captureAttribution(); }, []);

  const seoTitle = 'Emergency Plumbing & Electrical Services Miami | Dviso Construction';
  const seoDesc = lang === 'es'
    ? 'Servicio urgente de plomería y electricidad en Miami. Dviso Construction coordina asistencia de respuesta inmediata para problemas de plomería y electricidad en Miami y el Condado de Miami-Dade.'
    : 'Need urgent plumbing or electrical service in Miami? Dviso Construction provides immediate-response assistance for plumbing and electrical problems throughout Miami and Miami-Dade County.';

  const emergencyLd = {
    '@context': 'https://schema.org', '@type': 'Service',
    name: 'Emergency Plumbing & Electrical Services Miami',
    serviceType: 'Emergency plumbing and electrical service',
    provider: { '@type': 'GeneralContractor', name: 'Dviso Construction', url: 'https://www.multitrade-construction.com', telephone: BRAND.phone, email: BRAND.email },
    areaServed: { '@type': 'AdministrativeArea', name: 'Miami-Dade County, Florida' },
    url: 'https://www.multitrade-construction.com/emergency-services',
    description: seoDesc,
  };

  return (
    <div className="min-h-screen bg-[#0B0D0F]">
      <SEO path="/emergency-services" title={seoTitle} description={seoDesc} jsonLd={emergencyLd} />
      {/* Top bar */}
      <div className="border-b border-white/10 bg-[#0B0D0F]/90 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <a href="/" data-testid="emergency-back-home" className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors text-sm">
            <ArrowLeft className="w-4 h-4" />{e.back}
          </a>
          <div className="flex items-center gap-3">
            <div className="flex items-center rounded-md overflow-hidden text-xs font-bold border border-[#D4AF37]/60" data-testid="emergency-lang-selector">
              <button onClick={() => setLang('en')} className={`px-3 py-1.5 transition-colors ${lang === 'en' ? 'bg-[#D4AF37] text-black' : 'bg-[#1E232B] text-[#D4AF37] hover:bg-[#2A3038]'}`}>EN</button>
              <button onClick={() => setLang('es')} className={`px-3 py-1.5 transition-colors border-l border-[#D4AF37]/60 ${lang === 'es' ? 'bg-[#D4AF37] text-black' : 'bg-[#1E232B] text-[#D4AF37] hover:bg-[#2A3038]'}`}>ES</button>
            </div>
            <a href={`tel:${BRAND.phone}`} className="hidden sm:flex items-center gap-2 bg-[#E11D2A] hover:bg-[#C41623] text-white font-semibold text-sm px-4 py-2 rounded transition-colors">
              <Phone className="w-4 h-4" />{BRAND.phone}
            </a>
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="relative overflow-hidden border-b border-[#E11D2A]/20">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[40rem] h-[40rem] rounded-full bg-[#E11D2A]/10 blur-3xl" />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#E11D2A]/40 bg-[#E11D2A]/10 px-4 py-1.5 mb-6">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-[#E11D2A] opacity-75 animate-ping" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[#E11D2A]" />
            </span>
            <span className="text-[#FF6B73] text-xs font-mono tracking-[0.2em] uppercase">{e.badge}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white font-outfit flex items-center justify-center gap-3">
            <Siren className="w-9 h-9 sm:w-11 sm:h-11 text-[#E11D2A]" />
            {e.heading}
          </h1>
          <p className="text-lg text-slate-200 font-semibold mt-4">{e.tagline}</p>
          <p className="text-slate-400 mt-3 max-w-2xl mx-auto">{e.intro}</p>
          <div className="flex items-center justify-center gap-2 mt-5 text-[#FF6B73]">
            <Clock className="w-4 h-4" /><span className="text-sm font-semibold">{e.maxResponse}</span>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8 max-w-md mx-auto">
            <a href={`tel:${BRAND.phone}`} data-testid="emergency-page-call"
              className="flex-1 flex items-center justify-center gap-2 bg-[#E11D2A] hover:bg-[#C41623] text-white font-bold py-3.5 px-5 rounded-lg text-sm transition-all">
              <Phone className="w-4 h-4" />{e.callBtn}
            </a>
            <a href={`https://wa.me/${waNumber}`} target="_blank" rel="noopener noreferrer" data-testid="emergency-page-whatsapp"
              className="flex-1 flex items-center justify-center gap-2 border border-white/15 hover:border-white/30 bg-white/5 text-white font-semibold py-3.5 px-5 rounded-lg text-sm transition-all">
              <MessageCircle className="w-4 h-4" />{e.whatsappBtn}
            </a>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
        <div className="text-center mb-10">
          <h2 className="text-2xl sm:text-3xl font-bold text-white font-outfit">{e.formHeading}</h2>
          <p className="text-slate-400 mt-3">{e.formDesc}</p>
        </div>
        <EmergencyForm />
      </div>
      <Footer />
    </div>
  );
}
